/**
 * DepartmentSummaryPage - Public employee-facing department summary view.
 * Accessed via /dept-summary/{token}.
 */
import React, { useEffect, useState } from 'react';
import {
    Loader2, AlertTriangle, Users, Calendar, Clock, Shield,
    Star, HeartPulse, Activity, Cog, Monitor, MessageCircle,
    CheckCircle2, AlertCircle, Lightbulb
} from 'lucide-react';
import { API_BASE_URL } from '../services/api';
import '../styles/leadership-view.css';

function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function normalizeTerminology(value) {
    return String(value)
        .replace(/\bMicrosoft Teams\b/gi, 'Teams')
        .replace(/\bSlack\b/gi, 'Teams')
        .replace(/\bPeople Business Partners?\b/gi, 'People Business Partners')
        .replace(/\bPBP'?s\b/gi, 'People Business Partners')
        .replace(/\bPBP\b/gi, 'People Business Partners');
}

function toLabel(value) {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number') return normalizeTerminology(value);
    if (Array.isArray(value)) return value.map(toLabel).filter(Boolean).join(', ');
    if (typeof value === 'object') {
        const preferred = value.label || value.title || value.name || value.area || value.summary || value.task;
        if (preferred) return normalizeTerminology(preferred);
        const parts = Object.values(value).filter(v => typeof v === 'string' && v.trim()).slice(0, 2);
        return normalizeTerminology(parts.join(' - '));
    }
    return normalizeTerminology(value);
}

function asList(value) {
    return Array.isArray(value) ? value : [];
}

function prettyToken(value) {
    return toLabel(value).replace(/_/g, ' ');
}

function healthVariant(value) {
    const health = String(value || '').toLowerCase();
    if (['strong', 'high', 'positive', 'improving', 'adequate'].includes(health)) return 'positive';
    if (['moderate', 'mixed', 'stable', 'strained', 'needs_attention', 'needs_improvement'].includes(health)) return 'warning';
    if (['critical', 'concerning', 'elevated', 'high', 'low'].includes(health)) return 'danger';
    return 'default';
}

function Badge({ children, variant = 'default' }) {
    const styles = {
        default: { bg: '#e8f0f0', color: '#1a3a3a' },
        positive: { bg: '#e8f5e9', color: '#2e7d32' },
        warning: { bg: '#fff3e0', color: '#e65100' },
        danger: { bg: '#fde8e8', color: '#c62828' },
        info: { bg: '#e3f2fd', color: '#1565c0' },
    };
    const style = styles[variant] || styles.default;
    return <span className="lv-badge" style={{ background: style.bg, color: style.color }}>{children}</span>;
}

function SectionCard({ icon: Icon, iconColor, title, tag, children }) {
    return (
        <section className="lv-card">
            <h2 className="lv-card-title">
                {Icon && <Icon size={18} style={{ color: iconColor || '#1a3a3a' }} />}
                <span>{title}</span>
                {tag && <span className="lv-section-tag">{tag}</span>}
            </h2>
            <div className="lv-card-body">{children}</div>
        </section>
    );
}

function MiniCard({ title, children, badge, variant = 'default' }) {
    return (
        <div className="lv-mini-card">
            <div className="lv-mini-card-header">
                <strong>{title}</strong>
                {badge && <Badge variant={variant}>{badge}</Badge>}
            </div>
            {children}
        </div>
    );
}

function DepartmentSummaryPage({ token, previewData }) {
    const isPreview = Boolean(previewData);
    const [data, setData] = useState(previewData || null);
    const [loading, setLoading] = useState(!isPreview);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isPreview) {
            setData(previewData || null);
            setLoading(false);
            setError(null);
            return;
        }
        const loadSummary = async () => {
            try {
                setLoading(true);
                const res = await fetch(`${API_BASE_URL}/api/public/dept-summary/${token}`);
                if (res.status === 404) {
                    setError('Summary not found. This link may be invalid, expired, or revoked.');
                    return;
                }
                if (!res.ok) throw new Error('Failed to load department summary');
                setData(await res.json());
            } catch (err) {
                setError(err.message || 'Failed to load department summary');
            } finally {
                setLoading(false);
            }
        };
        loadSummary();
    }, [token, isPreview, previewData]);

    if (loading) {
        return (
            <div className="lv-page">
                <div className="lv-status-card">
                    <Loader2 size={28} className="lv-spinner" />
                    <p>Loading department summary...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="lv-page">
                <div className="lv-status-card">
                    <AlertTriangle size={28} style={{ color: '#c62828' }} />
                    <h2>Unable to Load Summary</h2>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (!data) return null;

    const summary = data.summary || {};
    const sentiment = summary.team_sentiment || {};
    const workload = summary.workload_health || {};
    const process = summary.process_themes || {};
    const technology = summary.technology_health || {};
    const collaboration = summary.collaboration_and_communication || {};
    const next90 = summary.next_90_days || {};

    const positives = [
        ...asList(process.process_strengths),
        ...asList(sentiment.positive_indicators),
        ...asList(collaboration.strengths),
    ];
    const frictionAreas = asList(process.top_friction_areas);
    const manualTasks = asList(process.repetitive_manual_tasks);
    const sentimentThemes = asList(sentiment.key_sentiment_themes);
    const concerns = [...asList(sentiment.areas_of_concern), ...asList(workload.key_concerns)];
    const techGroups = [
        { title: 'Working Well', items: asList(technology.tools_working_well), variant: 'positive', icon: CheckCircle2 },
        { title: 'Causing Friction', items: asList(technology.tools_causing_friction), variant: 'danger', icon: AlertTriangle },
        { title: 'Gaps to Watch', items: asList(technology.tool_gaps), variant: 'warning', icon: Lightbulb },
    ];

    return (
        <div className="lv-page">
            <div className="lv-container">
                <header className="lv-header">
                    <div className="lv-header-left">
                        <h1 className="lv-header-title">{data.department} Department Summary</h1>
                        <p className="lv-header-meta">
                            <Users size={14} />
                            <span>{data.respondent_count} respondent{data.respondent_count !== 1 ? 's' : ''}</span>
                            {data.generated_at && <span className="lv-meta-sep">&bull;</span>}
                            {data.generated_at && <><Calendar size={14} /><span>{formatDate(data.generated_at)}</span></>}
                            {data.expires_at && <span className="lv-meta-sep">&bull;</span>}
                            {data.expires_at && <><Clock size={14} /><span>Link expires {formatDate(data.expires_at)}</span></>}
                        </p>
                        {data.label && <p className="lv-header-label">{data.label}</p>}
                    </div>
                </header>

                <div className="lv-validation-banner">
                    <Shield size={16} />
                    <div>
                        <strong>Aggregated department view</strong>
                        <span>{summary.confidentiality_note || 'This summary contains department-level themes and excludes employee-identifying detail.'}</span>
                    </div>
                </div>

                {summary.executive_summary && (
                    <SectionCard icon={Star} iconColor="#d4a574" title="Summary">
                        <p className="lv-summary-text">{toLabel(summary.executive_summary)}</p>
                    </SectionCard>
                )}

                {(sentiment.overall_assessment || workload.capacity_assessment || process.efficiency_rating) && (
                    <SectionCard icon={Activity} iconColor="#7fb3b3" title="Department Snapshot">
                        <div className="lv-metric-row">
                            {sentiment.overall_assessment && (
                                <div className="lv-metric">
                                    <span className="lv-metric-label">Sentiment</span>
                                    <span className="lv-metric-value">{prettyToken(sentiment.overall_assessment)}</span>
                                </div>
                            )}
                            {workload.capacity_assessment && (
                                <div className="lv-metric">
                                    <span className="lv-metric-label">Capacity</span>
                                    <span className="lv-metric-value">{prettyToken(workload.capacity_assessment)}</span>
                                </div>
                            )}
                            {process.efficiency_rating && (
                                <div className="lv-metric">
                                    <span className="lv-metric-label">Process Flow</span>
                                    <span className="lv-metric-value">{prettyToken(process.efficiency_rating)}</span>
                                </div>
                            )}
                        </div>
                    </SectionCard>
                )}

                {positives.length > 0 && (
                    <SectionCard icon={CheckCircle2} iconColor="#2e7d32" title="What Is Working">
                        <div className="lv-whats-working-grid">
                            {positives.slice(0, 8).map((item, index) => (
                                <div key={index} className="lv-whats-working-card">
                                    <CheckCircle2 size={16} />
                                    <span>{toLabel(item)}</span>
                                </div>
                            ))}
                        </div>
                    </SectionCard>
                )}

                {(sentiment.morale_indicators || sentiment.engagement_level || sentimentThemes.length > 0 || concerns.length > 0) && (
                    <SectionCard icon={HeartPulse} iconColor="#c08a55" title="What We Heard">
                        <div className="lv-card-list">
                            {sentiment.morale_indicators && (
                                <MiniCard title="Morale Signals" badge={prettyToken(sentiment.overall_assessment)} variant={healthVariant(sentiment.overall_assessment)}>
                                    <p>{toLabel(sentiment.morale_indicators)}</p>
                                </MiniCard>
                            )}
                            {sentiment.engagement_level && (
                                <MiniCard title="Engagement" badge="theme">
                                    <p>{toLabel(sentiment.engagement_level)}</p>
                                </MiniCard>
                            )}
                            {sentimentThemes.length > 0 && (
                                <MiniCard title="Recurring Themes" badge={`${sentimentThemes.length} themes`} variant="info">
                                    {sentimentThemes.slice(0, 5).map((theme, index) => <p key={index}>{toLabel(theme)}</p>)}
                                </MiniCard>
                            )}
                            {concerns.length > 0 && (
                                <MiniCard title="Areas Needing Attention" badge={`${concerns.length} signals`} variant="warning">
                                    {concerns.slice(0, 5).map((concern, index) => <p key={index}>{toLabel(concern)}</p>)}
                                </MiniCard>
                            )}
                        </div>
                    </SectionCard>
                )}

                {(process.efficiency_analysis || frictionAreas.length > 0 || manualTasks.length > 0) && (
                    <SectionCard icon={Cog} iconColor="#7fb3b3" title="Where Work Gets Harder">
                        {process.efficiency_analysis && <p className="lv-summary-text">{toLabel(process.efficiency_analysis)}</p>}
                        {frictionAreas.length > 0 && (
                            <div className="lv-subsection">
                                <h3 className="lv-subsection-title">Friction Areas</h3>
                                <div className="lv-friction-rank-grid">
                                    {frictionAreas.map((item, index) => (
                                        <div key={index} className="lv-friction-visual-card lv-friction-warning">
                                            <div className="lv-friction-rank">#{index + 1}</div>
                                            <div className="lv-friction-visual-body">
                                                <div className="lv-friction-visual-header">
                                                    <h4><AlertCircle size={15} /> {toLabel(item.area) || 'Friction Area'}</h4>
                                                </div>
                                                {item.detailed_description && <p className="lv-friction-desc">{toLabel(item.detailed_description)}</p>}
                                                <div className="lv-friction-field-grid">
                                                    {item.prevalence && <div className="lv-friction-field"><span>Seen In</span><strong>{toLabel(item.prevalence)}</strong></div>}
                                                    {item.impact && <div className="lv-friction-field lv-friction-field-impact"><span>Impact</span><strong>{toLabel(item.impact)}</strong></div>}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {manualTasks.length > 0 && (
                            <div className="lv-subsection">
                                <h3 className="lv-subsection-title">Manual Work Patterns</h3>
                                <div className="lv-card-list">
                                    {manualTasks.map((task, index) => (
                                        <MiniCard key={index} title={toLabel(task.task) || 'Manual task'} badge={toLabel(task.frequency)} variant="warning">
                                            {task.why_manual && <p>{toLabel(task.why_manual)}</p>}
                                        </MiniCard>
                                    ))}
                                </div>
                            </div>
                        )}
                    </SectionCard>
                )}

                {(technology.technology_overview || techGroups.some(group => group.items.length > 0)) && (
                    <SectionCard icon={Monitor} iconColor="#1565c0" title="Tools and Systems">
                        {technology.technology_overview && <p className="lv-summary-text">{toLabel(technology.technology_overview)}</p>}
                        <div className="lv-tech-grid">
                            {techGroups.filter(group => group.items.length > 0).map(group => {
                                const Icon = group.icon;
                                return (
                                    <div key={group.title} className={`lv-tech-card lv-tech-${group.variant}`}>
                                        <div className="lv-tech-card-header">
                                            <Icon size={16} />
                                            <strong>{group.title}</strong>
                                            <Badge variant={group.variant}>{group.items.length}</Badge>
                                        </div>
                                        <div className="lv-tech-items">
                                            {group.items.map((item, index) => <div key={index} className="lv-tech-item">{toLabel(item)}</div>)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {technology.training_needs && <p className="lv-friction-desc">{toLabel(technology.training_needs)}</p>}
                    </SectionCard>
                )}

                {(collaboration.team_dynamics || collaboration.cross_team_collaboration || collaboration.communication_effectiveness || collaboration.meeting_culture) && (
                    <SectionCard icon={MessageCircle} iconColor="#7fb3b3" title="Collaboration and Communication">
                        <div className="lv-collab-grid">
                            {[
                                ['Team Dynamics', collaboration.team_dynamics, 'positive'],
                                ['Cross-Team Collaboration', collaboration.cross_team_collaboration, 'warning'],
                                ['Communication', collaboration.communication_effectiveness, 'info'],
                                ['Meeting Culture', collaboration.meeting_culture, 'default'],
                            ].filter(([, value]) => value).map(([title, value, variant]) => (
                                <div key={title} className={`lv-collab-card lv-collab-${variant}`}>
                                    <div className="lv-collab-card-header"><strong>{title}</strong></div>
                                    <p>{toLabel(value)}</p>
                                </div>
                            ))}
                        </div>
                    </SectionCard>
                )}

                {(next90.summary || asList(next90.priorities).length > 0 || asList(next90.opportunities).length > 0) && (
                    <SectionCard icon={Lightbulb} iconColor="#d4a574" title="Next 90 Days" tag={prettyToken(next90.expected_trajectory)}>
                        {next90.summary && <p className="lv-summary-text">{toLabel(next90.summary)}</p>}
                        <div className="lv-card-list">
                            {asList(next90.priorities).length > 0 && (
                                <MiniCard title="Likely Focus Areas" badge={`${asList(next90.priorities).length} areas`} variant="info">
                                    {asList(next90.priorities).map((item, index) => <p key={index}>{toLabel(item)}</p>)}
                                </MiniCard>
                            )}
                            {asList(next90.opportunities).length > 0 && (
                                <MiniCard title="Opportunities" badge={`${asList(next90.opportunities).length} signals`} variant="positive">
                                    {asList(next90.opportunities).map((item, index) => <p key={index}>{toLabel(item)}</p>)}
                                </MiniCard>
                            )}
                            {next90.success_indicators && (
                                <MiniCard title="What Better Could Look Like" badge="signal" variant="positive">
                                    <p>{toLabel(next90.success_indicators)}</p>
                                </MiniCard>
                            )}
                        </div>
                    </SectionCard>
                )}
            </div>
        </div>
    );
}

export default DepartmentSummaryPage;